import type { MarketPoolQuoteSnapshot } from '@morfi/market-math';
import {
  effectiveResolutionThresholdPpbFromPolicy,
  resolutionThresholdFloorPpb,
} from '../math/resolution-threshold.js';
import { projectResolutionAt } from '../math/resolution-threshold-projection.js';

const PPB_ONE = 1_000_000_000n;

/** Project the contract's earliest resolution second from one coherent market snapshot. */
export function projectMarketPoolResolutionAt(snapshot: MarketPoolQuoteSnapshot): string | null {
  const gate = snapshot.bondingCurveTradeGate;
  if (gate === null) throw new Error('Missing trading conviction gate');
  const reserves = snapshot.outcomes.map((outcome) =>
    BigInt(requiredValue(outcome.realQuoteReserveD6)),
  );
  const baseline = BigInt(gate.convictionBaselineReserveQuoteD6);
  const counted = reserves.map((reserve) => reserve + baseline);
  const total = counted.reduce((sum, reserve) => sum + reserve, 0n);
  const targets =
    total === 0n
      ? counted.map(() => PPB_ONE / BigInt(counted.length))
      : counted.map((reserve) => (reserve * PPB_ONE) / total);
  const policy = {
    initialThresholdPpb: BigInt(gate.convictionResolutionThresholdPpb),
    thresholdFloorPpb: resolutionThresholdFloorPpb(
      BigInt(counted.length),
      BigInt(requiredValue(gate.resolutionThresholdFloorMarginPpb)),
    ),
    tradingStartedTs: BigInt(requiredValue(gate.tradingStartedUnixSeconds)),
    decayDelaySecs: BigInt(requiredValue(gate.resolutionThresholdDecayDelaySecs)),
    dropIntervalSecs: BigInt(requiredValue(gate.resolutionThresholdDropIntervalSecs)),
    dropPpb: BigInt(requiredValue(gate.resolutionThresholdDropPpb)),
  };
  const anchor = BigInt(gate.lastConvictionUpdateUnixSeconds);
  effectiveResolutionThresholdPpbFromPolicy(policy, anchor);
  const convictionPpb = gate.outcomeConvictionPpb.map((value) => BigInt(value));
  const retentionPpb = BigInt(gate.convictionRetentionPerSecondPpb);
  if (
    anchor < policy.tradingStartedTs ||
    retentionPpb <= 0n ||
    retentionPpb >= PPB_ONE ||
    convictionPpb.some((value) => value < 0n || value > PPB_ONE)
  ) {
    throw new Error('Invalid indexed conviction anchor');
  }
  return (
    projectResolutionAt(
      { convictionPpb, targetPpb: targets, retentionPpb, updateTsSecs: anchor },
      policy,
    )?.resolutionAtSecs.toString() ?? null
  );
}

function requiredValue(value: string | null | undefined): string {
  if (value == null) throw new Error('Missing indexed resolution policy');
  return value;
}
